import { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Users } from 'lucide-react';
import RandomTutorCard from './RandomTutorCard';
import { Skeleton } from './ui/skeleton';
import { pb } from '@/lib/pocketbase';

interface RandomTutorsShowcaseProps {
  tutors: any[];
  isLoading?: boolean;
}

const RandomTutorsShowcase = ({ tutors, isLoading = false }: RandomTutorsShowcaseProps) => {
  const [randomTutors, setRandomTutors] = useState<any[]>([]);

  // Fetch hero tutor card IDs to exclude them
  const { data: heroTutorIds, isLoading: heroIdsLoading } = useQuery({
    queryKey: ['hero-tutor-ids-exclude'],
    queryFn: async () => {
      try {
        const records = await pb.collection('hero_tutor_cards').getFullList({
          filter: 'enabled = true',
          fields: 'tutorId',
          requestKey: `hero-ids-exclude-${Date.now()}`,
        });
        return records.map((r: any) => r.tutorId);
      } catch (error) {
        return [];
      }
    },
    staleTime: 1000 * 60 * 5,
  });

  // Function to get 8 random tutors from highly evaluated ones (evaluation > 8)
  const getRandomTutors = (tutorsList: any[], excludeIds: string[], count: number = 8) => {
    // First exclude hero tutor card tutors
    const availableTutors = tutorsList.filter(t => !excludeIds.includes(t.id));
    
    // Filter to only include highly evaluated tutors (evaluation > 8)
    const highlyEvaluated = availableTutors.filter(t => (t.evaluation || 0) > 8);
    
    // If not enough highly evaluated tutors, use all available
    const sourceList = highlyEvaluated.length >= count ? highlyEvaluated : availableTutors;
    
    if (sourceList.length <= count) {
      return sourceList;
    }
    
    const shuffled = [...sourceList].sort(() => 0.5 - Math.random());
    return shuffled.slice(0, count);
  };

  // Update random tutors only once when both tutors and hero IDs are ready
  useEffect(() => {
    if (tutors.length > 0 && !heroIdsLoading && heroTutorIds !== undefined) {
      const selected = getRandomTutors(tutors, heroTutorIds, 8);
      setRandomTutors(selected);
    }
  }, [tutors, heroTutorIds, heroIdsLoading]);

  if (isLoading || heroIdsLoading) {
    return (
      <div className="mt-8">
        <div className="text-center mb-4">
          <div className="flex items-center justify-center gap-2 mb-2">
            <Users className="h-4 w-4 text-blue-600" />
            <h3 className="text-lg md:text-xl font-bold text-gray-900">
              Meet Our Expert Tutors
            </h3>
          </div>
          <p className="text-gray-600 text-sm">
            Discover amazing tutors ready to help you learn
          </p>
        </div>

        <div className="grid grid-cols-4 md:grid-cols-4 lg:grid-cols-6 gap-1 md:gap-4 lg:gap-6">
          {Array.from({ length: 6 }).map((_, index) => (
            <div 
              key={index} 
              className={index >= 4 ? 'hidden lg:block' : ''}
            >
              <div className="flex flex-col items-center space-y-2">
                <Skeleton className="w-16 h-16 md:w-20 md:h-20 lg:w-24 lg:h-24 rounded-full" />
                <Skeleton className="h-3 w-16 md:w-20" />
                <Skeleton className="h-2 w-12 md:w-16" />
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (randomTutors.length === 0) {
    return null;
  }

  return (
    <div className="mt-8">
      <div className="text-center mb-4">
        <div className="flex items-center justify-center gap-2 mb-2">
          <Users className="h-4 w-4 text-blue-600" />
          <h3 className="text-lg md:text-xl font-bold text-gray-900">
            Meet Our Expert Tutors
          </h3>
        </div>
        <p className="text-gray-600 text-sm">
          Discover amazing tutors ready to help you learn
        </p>
      </div>

      <div className="grid grid-cols-4 md:grid-cols-4 lg:grid-cols-6 gap-1 md:gap-4 lg:gap-6">
        {randomTutors.slice(0, 6).map((tutor, index) => (
          <div 
            key={tutor.id} 
            className={index >= 4 ? 'hidden lg:block' : ''}
          >
            <RandomTutorCard tutor={tutor} />
          </div>
        ))}
      </div>
    </div>
  );
};

export default RandomTutorsShowcase;
